import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Heart, Shield, Video, Home } from 'lucide-react';

const LandingPage = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#fcf6e3' }}>
            {/* Header */}
            <header className="glass-header sticky top-0 z-50 py-4">
                <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                            MVC VET TELECARE
                        </h1>
                        <p className="text-sm" style={{ color: '#d9a520' }}>Licensed Veterinarians</p>
                    </div>
                    <div className="flex gap-3">
                        <Button 
                            variant="outline"
                            onClick={() => navigate('/login')} 
                            data-testid="medical-records-btn"
                        >
                            Medical Records
                        </Button>
                        <Button 
                            onClick={() => navigate('/booking')} 
                            data-testid="book-now-header-btn"
                            className="btn-secondary"
                        >
                            Book Consultation
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" 
                            style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                        >
                            Online Vet Consultation,{' '}
                            <span style={{ fontStyle: 'italic', color: '#d9a520' }}>24/7</span>
                        </h2>
                        <p className="text-lg sm:text-xl mb-6" style={{ color: '#2d2a2a' }}>
                            Expert consultation with our licensed veterinarians for your dogs and cats without leaving your home.
                        </p>
                        
                        {/* Trust Badges */}
                        <div className="bg-white p-6 rounded-lg shadow-md mb-6" style={{ border: '2px solid #d9a520' }}>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <Shield className="w-5 h-5" style={{ color: '#d9a520' }} />
                                    <span className="font-semibold" style={{ color: '#701a25' }}>Licensed Veterinarian</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Heart className="w-5 h-5" style={{ color: '#d9a520' }} />
                                    <span className="font-semibold" style={{ color: '#701a25' }}>Real Clinic, Not Just Online</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Shield className="w-5 h-5" style={{ color: '#d9a520' }} />
                                    <span className="font-semibold" style={{ color: '#701a25' }}>Evidence-Based Veterinary Care</span>
                                </div>
                                <p className="text-xs pt-2 border-t" style={{ color: '#5c5555' }}>
                                    Serving pets with compassion and professionalism<br/>
                                    <strong>Malate Veterinary Clinic</strong> | 9am-7pm
                                </p>
                            </div>
                        </div>

                        <Button 
                            onClick={() => navigate('/booking')} 
                            data-testid="book-consultation-btn"
                            className="btn-primary text-lg py-6 px-8"
                        >
                            Book Online Consultation - ₱499
                        </Button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative"
                    >
                        <img
                            src="https://images.pexels.com/photos/7470631/pexels-photo-7470631.jpeg"
                            alt="Veterinarian with Dog"
                            className="rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full"
                            style={{ border: '4px solid #701a25' }}
                        />
                    </motion.div>
                </div>
            </section>

            {/* MVC Vet Telecare Section */}
            <section className="py-16 px-6" style={{ backgroundColor: 'white' }}>
                <div className="max-w-6xl mx-auto text-center">
                    <h3 
                        className="text-3xl sm:text-4xl font-bold mb-4" 
                        style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                    >
                        MVC Vet Telecare
                    </h3>
                    <p className="text-lg mb-8 max-w-3xl mx-auto" style={{ color: '#2d2a2a' }}>
                        Professional online veterinary consultations for your dogs and cats. 
                        Get expert advice, treatment recommendations from the comfort of your home.
                    </p>
                </div>
            </section>

            {/* Consultation Types */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <h3 
                        className="text-3xl sm:text-4xl font-bold text-center mb-12" 
                        style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                    >
                        Our Services
                    </h3>
                    <div className="grid md:grid-cols-2 gap-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                            className="card p-8"
                            data-testid="general-consultation-card"
                        >
                            <div className="flex justify-center mb-4">
                                <Video className="w-16 h-16" style={{ color: '#d9a520' }} />
                            </div>
                            <h4 
                                className="text-2xl font-semibold mb-3 text-center" 
                                style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            >
                                General Online Consultation
                            </h4>
                            <p className="text-3xl font-bold text-center mb-4" style={{ color: '#d9a520' }}>₱499</p>
                            <p className="text-center mb-4" style={{ color: '#5c5555' }}>
                                Available: 9:00 AM - 7:00 PM
                            </p>
                            <ul className="space-y-2" style={{ color: '#2d2a2a' }}>
                                <li>• Health check-ups and consultations</li>
                                <li>• Treatment recommendations</li>
                                <li>• Medication guidance</li>
                                <li>• Digital prescription issuance</li>
                                <li>• Behavioral concerns</li>
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                            className="card p-8"
                            style={{ border: '2px solid #d9a520' }}
                            data-testid="emergency-consultation-card"
                        >
                            <div className="flex justify-center mb-4">
                                <Clock className="w-16 h-16" style={{ color: '#701a25' }} />
                            </div>
                            <h4 
                                className="text-2xl font-semibold mb-3 text-center" 
                                style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                            >
                                Emergency Consultation
                            </h4>
                            <p className="text-3xl font-bold text-center mb-4" style={{ color: '#701a25' }}>₱799</p>
                            <p className="text-center mb-4" style={{ color: '#5c5555' }}>
                                Available: 7:00 PM - 9:00 AM
                            </p>
                            <ul className="space-y-2" style={{ color: '#2d2a2a' }}>
                                <li>• Urgent health concerns</li>
                                <li>• After-hours emergencies</li>
                                <li>• Immediate guidance needed</li>
                                <li>• Emergency prescriptions</li>
                                <li>• Critical symptom assessment</li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 px-6" style={{ backgroundColor: 'white' }}>
                <div className="max-w-6xl mx-auto">
                    <h3 
                        className="text-3xl sm:text-4xl font-bold text-center mb-12" 
                        style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                    >
                        Why Choose Online Consultation
                    </h3>
                    <div className="grid md:grid-cols-4 gap-8">
                        {[
                            {
                                icon: <Home className="w-12 h-12" style={{ color: '#d9a520' }} />,
                                title: 'Convenience',
                                description: 'No need to travel or stress your pet with clinic visits'
                            },
                            {
                                icon: <Clock className="w-12 h-12" style={{ color: '#d9a520' }} />,
                                title: '24/7 Availability',
                                description: 'Book consultations anytime, including emergency hours'
                            },
                            {
                                icon: <Heart className="w-12 h-12" style={{ color: '#d9a520' }} />,
                                title: 'Expert Care',
                                description: 'Licensed veterinarians with years of experience'
                            },
                            {
                                icon: <Shield className="w-12 h-12" style={{ color: '#d9a520' }} />,
                                title: 'Digital Prescriptions',
                                description: 'Receive prescriptions digitally after consultation'
                            }
                        ].map((feature, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                viewport={{ once: true }}
                                className="card p-6 text-center"
                                data-testid={`feature-card-${idx}`}
                            >
                                <div className="flex justify-center mb-4">{feature.icon}</div>
                                <h4 
                                    className="text-xl font-semibold mb-3" 
                                    style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}
                                >
                                    {feature.title}
                                </h4>
                                <p style={{ color: '#5c5555' }}>{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Image Gallery */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            'https://images.pexels.com/photos/6235228/pexels-photo-6235228.jpeg',
                            'https://images.pexels.com/photos/6235024/pexels-photo-6235024.jpeg',
                            'https://images.pexels.com/photos/7474855/pexels-photo-7474855.jpeg'
                        ].map((img, idx) => (
                            <motion.img
                                key={idx}
                                src={img}
                                alt={`Veterinarian with pet ${idx + 1}`}
                                className="rounded-xl shadow-lg w-full h-64 object-cover"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.4, delay: idx * 0.1 }}
                                viewport={{ once: true }}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6" style={{ backgroundColor: '#701a25' }}>
                <div className="max-w-4xl mx-auto text-center">
                    <h3 
                        className="text-3xl sm:text-4xl font-bold mb-6" 
                        style={{ fontFamily: 'Playfair Display, serif', color: '#fcf6e3' }}
                    >
                        Ready to Book Your Online Consultation?
                    </h3>
                    <p className="text-lg mb-8" style={{ color: '#fcf6e3', opacity: 0.9 }}>
                        Get professional veterinary care for your dog or cat from home.
                    </p>
                    <Button 
                        onClick={() => navigate('/booking')} 
                        data-testid="cta-book-now-btn"
                        className="btn-secondary text-lg py-6 px-8"
                    >
                        Book Now - Starting at ₱499
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-6" style={{ backgroundColor: '#701a25' }}>
                <div className="max-w-6xl mx-auto text-center">
                    <p style={{ color: '#fcf6e3' }}>
                        © 2025 MVC Vet Telecare - Licensed Veterinarians
                    </p>
                    <p className="mt-2" style={{ color: '#fcf6e3', opacity: 0.8 }}>
                        Available 24/7 for online consultations
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;